import { toast } from 'react-hot-toast';

/**
 * Custom toast utilities for different scenarios
 */

// Generic close button component
const CloseButton = ({ onClick }) => (
    <button
        onClick={onClick}
        style={{
            marginLeft: '10px',
            cursor: 'pointer',
            border: 'none',
            background: 'transparent',
            fontSize: '16px',
            padding: '2px 6px',
            borderRadius: '50%',
            lineHeight: 1,
            color: '#333',
        }}
        type="button"
    >
        ×
    </button>
);

// Success toast with dismiss button
const successToast = (message, options = {}) => {
    const { duration = 2500, onClose } = options;

    return toast((t) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#10B981', marginRight: '8px' }}>✓</span>
                <span>{message}</span>
            </div>
            <CloseButton
                onClick={() => {
                    toast.dismiss(t.id);
                    if (onClose) onClose();
                }}
            />
        </div>
    ), {
        position: "top-center",
        duration: duration
    });
};

// Error toast with dismiss button
const errorToast = (message, options = {}) => {
    const { duration = 3000, onClose } = options;

    return toast((t) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#EF4444', marginRight: '8px' }}>✕</span>
                <span>{message}</span>
            </div>
            <CloseButton
                onClick={() => {
                    toast.dismiss(t.id);
                    if (onClose) onClose();
                }}
            />
        </div>
    ), {
        position: "top-center",
        duration: duration
    });
};

// Warning toast with dismiss button
const warningToast = (message, options = {}) => {
    const { duration = 3000, onClose } = options;

    return toast((t) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#F59E0B', marginRight: '8px' }}>⚠</span>
                <span>{message}</span>
            </div>
            <CloseButton
                onClick={() => {
                    toast.dismiss(t.id);
                    if (onClose) onClose();
                }}
            />
        </div>
    ), {
        position: "top-center",
        duration: duration
    });
};

// Info toast with dismiss button
const infoToast = (message, options = {}) => {
    const { duration = 2500, onClose } = options;

    return toast((t) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#3B82F6', marginRight: '8px' }}>ℹ</span>
                <span>{message}</span>
            </div>
            <CloseButton
                onClick={() => {
                    toast.dismiss(t.id);
                    if (onClose) onClose();
                }}
            />
        </div>
    ), {
        position: "top-center",
        duration: duration
    });
};

// Action toast with confirm and cancel buttons
const actionToast = (message, onConfirm, options = {}) => {
    const {
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        duration = 5000,
        onClose
    } = options;

    return toast((t) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{message}</span>
                <CloseButton
                    onClick={() => {
                        toast.dismiss(t.id);
                        if (onClose) onClose();
                    }}
                />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                    onClick={() => {
                        toast.dismiss(t.id);
                        if (onClose) onClose();
                    }}
                    style={{
                        cursor: 'pointer',
                        border: '1px solid #D1D5DB',
                        background: '#F9FAFB',
                        borderRadius: '4px',
                        padding: '4px 12px',
                        fontSize: '14px',
                        color: '#374151',
                    }}
                    type="button"
                >
                    {cancelText}
                </button>
                <button
                    onClick={() => {
                        onConfirm();
                        toast.dismiss(t.id);
                        if (onClose) onClose();
                    }}
                    style={{
                        cursor: 'pointer',
                        border: 'none',
                        background: '#3B82F6',
                        borderRadius: '4px',
                        padding: '4px 12px',
                        fontSize: '14px',
                        color: 'white',
                    }}
                    type="button"
                >
                    {confirmText}
                </button>
            </div>
        </div>
    ), {
        position: "top-center",
        duration: duration
    });
};

// Loading toast that can be updated with progress or dismissed on completion
const loadingToast = (message, promise, options = {}) => {
    const { onClose } = options;

    return toast.promise(
        promise.finally(() => {
            if (onClose) setTimeout(onClose, 300);
        }),
        {
            loading: message,
            success: (data) => data?.message || 'Completed successfully!',
            error: (err) => err?.message || 'An error occurred',
        },
        {
            position: "top-center",
            style: {
                minWidth: '250px',
            },
        }
    );
};

// Notification toast for system messages
const notificationToast = (message, options = {}) => {
    const { duration = 3000, onClose } = options;

    return toast((t) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ color: '#6B7280', marginRight: '8px' }}>🔔</span>
                <span>{message}</span>
            </div>
            <CloseButton
                onClick={() => {
                    toast.dismiss(t.id);
                    if (onClose) onClose();
                }}
            />
        </div>
    ), {
        position: "top-center",
        duration: duration
    });
};

// Simple toast for basic messages (example from code snippet)
const simpleToast = (message, options = {}) => {
    const { duration = 2500, onClose } = options;

    return toast((t) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{message}</span>
            {onClose !== false && (
                <CloseButton
                    onClick={() => {
                        toast.dismiss(t.id);
                        if (onClose) onClose();
                    }}
                />
            )}
        </div>
    ), {
        position: "top-center",
        duration: duration
    });
};

// Custom toast with full control
const customToast = (content, options = {}) => {
    const { duration = 2500 } = options;

    return toast(content, {
        position: "top-center",
        duration: duration,
        ...options
    });
};

// Dismiss all toasts
const dismissAll = () => {
    toast.dismiss();
};

export {
    successToast,
    errorToast,
    warningToast,
    infoToast,
    actionToast,
    loadingToast,
    notificationToast,
    simpleToast,
    customToast,
    dismissAll
};

export default {
    success: successToast,
    error: errorToast,
    warning: warningToast,
    info: infoToast,
    action: actionToast,
    loading: loadingToast,
    notification: notificationToast,
    simple: simpleToast,
    custom: customToast,
    dismissAll
};
